import React from 'react';
import { create } from 'zustand';

import { createFormStateSlice } from './slices/form-state.slice';
import { createSeedSlice } from './slices/seed.slice';
import { createDraftPersistenceSlice } from './slices/draft-persistence.slice';
import { initialFormData, initialSeed } from './slices/internals';
import type { RenderStore, RenderFormData } from './slices/types';

// Re-export types + per-tab baseline helpers (consumed by
// `render-form-store.selectors.ts`).
export type { RenderStore, RenderFormData };
export {
  getExploreBaseline,
  getRefineBaseline,
} from './slices/internals';

/* ============================================================================
 * RENDER FORM SPEC — defaults, local state, post-submit behavior
 * ============================================================================
 *
 * The Render Settings sheet has two independent tabs: Explore (Recreate) and
 * Refine. Each tab keeps its own local form state for the DESIGN fields
 * (prompts, style/material/color references, controls, environment, …) —
 * those are mode-keyed and fully independent intents. The TECHNICAL group
 * (`SHARED_TECH_SETTINGS_KEYS` in slices/internals.ts: model family, effort,
 * extended thinking, output style, Hi-Res) is the one exception: a single
 * shared slot for both modes, persisted per context WITHOUT a mode suffix —
 * flipping Hi-Res in Explore shows in Refine and vice versa.
 *
 * `render.settings` on the API is an immutable snapshot of how a render was
 * produced. The "default vs local draft vs after-submit" behavior is purely
 * a UX concern and lives entirely on the client.
 *
 * 1) DEFAULTS (shown when the user has no local draft for that tab):
 *
 *      Source render's mode is Explore:
 *          Explore default = settings of the source render
 *          Refine  default = blank
 *
 *      Source render's mode is Refine:
 *          Explore default = blank
 *          Refine  default = blank (revealed only via the refinement badge)
 *
 *    A Refine source render does NOT seed the Explore tab. Bleeding refine
 *    fields (notably the prompt) into Explore as "defaults" is a bug — this
 *    spec exists to prevent exactly that.
 *
 * 2) LOCAL STATE (the user's draft):
 *
 *    Persisted per (concept, angle, source render, mode). Switching tabs
 *    swaps the visible draft. Editing one tab never touches the other.
 *
 * 3) AFTER SUBMIT EXPLORE:
 *
 *      Explore tab: local draft is cleared. The just-submitted settings now
 *                   ARE the new render's Explore default — the form shows
 *                   them as a baseline, not as "user changes pending".
 *      Refine  tab: local draft is preserved untouched.
 *
 * 4) AFTER SUBMIT REFINE:
 *
 *      Explore tab: local draft is preserved untouched.
 *      Refine  tab: local draft is preserved untouched (!). Submitting a
 *                   refine does NOT discard the user's text. Refine is
 *                   iterative — the user often wants to refine further from
 *                   the same starting prompt. Never erase user input that
 *                   is not stored anywhere else.
 *
 * Mirrored by Apple `RenderManager.swift` and Android `RenderFormManager.kt`.
 * ==========================================================================*/

export const useRenderStore = create<RenderStore>()((...a) => ({
  ...createFormStateSlice(...a),
  ...createSeedSlice(...a),
  ...createDraftPersistenceSlice(...a),
}));

// ===== Selector hooks =====
export const useFormData = () => useRenderStore((state) => state.formData);

export const useSelectedControlIds = () => {
  const controls = useRenderStore((state) => state.formData.controls);
  return React.useMemo(() => controls || [], [controls]);
};

export const useSelectedReferences = () =>
  useRenderStore((state) => state.selectedReferences);

export const useSeed = () => useRenderStore((state) => state.formData.seed);

export const useSelectedRenderId = () =>
  useRenderStore((state) => state.selectedRenderId);

export const useShowRenderDetails = () =>
  useRenderStore((state) => state.showRenderDetails);

export const useHasChanges = () => {
  const formData = useFormData();
  const defaultValues = useRenderStore((state) => state.defaultValues);
  const isDerivative = formData.is_derivative;

  // For derivative angles, always consider changes if there are no default values
  // This handles the case when an angle has no previous renders
  if (!defaultValues) {
    if (isDerivative) {
      // Check if any derivative-specific fields have been modified
      return !!(
        formData.prompts?.prompt_angle ||
        formData.controls?.length ||
        formData.seed !== initialSeed
      );
    }
    return false;
  }

  const combinedDefaults = { ...initialFormData, ...defaultValues };
  return JSON.stringify(formData) !== JSON.stringify(combinedDefaults);
};

export const useIsValidForSubmit = () => {
  const version = useRenderStore((state) => state.formData.version);
  return version > 0;
};

export const useFormDataWithSeed = () => {
  const formData = useFormData();
  return formData;
};
